import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2, ShieldCheck, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PrimeVisionLogo } from "@/components/PrimeVisionLogo";

export default function Login() {
  const { user, login, verifyMfa, requestPasswordless, verifyPasswordless } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("password"); // 'password' | 'otp'
  const [step, setStep] = useState("form"); // form | mfa | otpsent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [mfaChallenge, setMfaChallenge] = useState(null); // {id, isAdmin}
  const [otpCode, setOtpCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  // Math challenge anti-bot (visibile solo dopo 2 errori per non disturbare l'utente reale)
  const [challenge] = useState(() => {
    const a = Math.floor(Math.random() * 8) + 2;
    const b = Math.floor(Math.random() * 8) + 2;
    return { a, b, answer: a + b };
  });
  const [challengeInput, setChallengeInput] = useState("");
  const [honeypot, setHoneypot] = useState(""); // honeypot anti-bot: deve restare vuoto

  // Cloudflare Turnstile (anti-bot esterno). Si abilita server-side se TURNSTILE_SITE_KEY+SECRET_KEY presenti.
  const [tsConfig, setTsConfig] = useState({ enabled: false, site_key: null });
  const [tsToken, setTsToken] = useState("");
  const tsWidgetRef = useRef(null);
  const tsWidgetIdRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/turnstile-config");
        if (mounted) setTsConfig(data);
      } catch {
        // se l'endpoint fallisce trattiamo come non configurato (preview/dev safe)
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Carica script Turnstile e renderizza il widget quando enabled
  useEffect(() => {
    if (!tsConfig.enabled || !tsConfig.site_key) return;
    const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    const renderWidget = () => {
      if (!window.turnstile || !tsWidgetRef.current) return;
      if (tsWidgetIdRef.current) {
        try { window.turnstile.remove(tsWidgetIdRef.current); } catch { /* noop */ }
        tsWidgetIdRef.current = null;
      }
      tsWidgetIdRef.current = window.turnstile.render(tsWidgetRef.current, {
        sitekey: tsConfig.site_key,
        callback: (token) => setTsToken(token || ""),
        "error-callback": () => setTsToken(""),
        "expired-callback": () => setTsToken(""),
        theme: "light",
        size: "flexible",
      });
    };
    if (window.turnstile) {
      renderWidget();
      return;
    }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", renderWidget, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener("load", renderWidget, { once: true });
    document.head.appendChild(s);
    return () => {
      if (tsWidgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(tsWidgetIdRef.current); } catch { /* noop */ }
        tsWidgetIdRef.current = null;
      }
    };
  }, [tsConfig.enabled, tsConfig.site_key]);

  if (user) return <Navigate to="/" replace />;

  const errMsg = (err) => {
    const d = err?.response?.data?.detail;
    if (!d) return "Email o password non corretti";
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
    if (typeof d === "string") return d;
    return JSON.stringify(d);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (honeypot) { toast.error("Richiesta sospetta bloccata"); return; }
    if (cooldownUntil > Date.now()) {
      const sec = Math.ceil((cooldownUntil - Date.now()) / 1000);
      toast.error(`Troppi tentativi. Riprova fra ${sec}s.`);
      return;
    }
    if (failCount >= 2) {
      const expected = challenge.answer;
      if (parseInt(challengeInput, 10) !== expected) {
        toast.error("Verifica anti-bot non superata");
        return;
      }
    }
    if (tsConfig.enabled && !tsToken) {
      toast.error("Completa la verifica anti-bot per continuare");
      return;
    }
    setBusy(true);
    try {
      const res = await login(email, password, tsToken || undefined);
      setFailCount(0);
      if (res.mfa_required) {
        setMfaChallenge({ id: res.mfa_challenge_id, isAdmin: res.pending_user?.role === "admin" });
        setStep("mfa");
        toast.success("Codice di verifica inviato via SMS e email");
      } else {
        toast.success("Benvenuto in PrimeVision Software");
        nav("/");
      }
    } catch (err) {
      const next = failCount + 1;
      setFailCount(next);
      toast.error(errMsg(err));
      // Reset token Turnstile (è single-use) per forzare nuova verifica al prossimo submit
      if (tsConfig.enabled && window.turnstile && tsWidgetIdRef.current) {
        try { window.turnstile.reset(tsWidgetIdRef.current); } catch { /* noop */ }
        setTsToken("");
      }
      if (next >= 5) {
        setCooldownUntil(Date.now() + 60_000);
        toast.error("Account temporaneamente bloccato 60s per troppi tentativi");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e?.preventDefault();
    if (!otpCode.trim() || otpCode.trim().length < 4) {
      toast.error("Inserisci il codice ricevuto");
      return;
    }
    setBusy(true);
    try {
      await verifyMfa(mfaChallenge.id, otpCode.trim(), trustDevice);
      toast.success("Autenticazione a due fattori completata");
      nav("/");
    } catch (err) {
      toast.error(errMsg(err) || "Codice non valido");
    } finally {
      setBusy(false);
    }
  };

  const submitOtpRequest = async (e) => {
    e?.preventDefault();
    if (!email) { toast.error("Inserisci la tua email"); return; }
    setBusy(true);
    try {
      const r = await requestPasswordless(email);
      toast.success(r?.message || "Se l'account è valido riceverai un codice");
      setStep("otpsent");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const submitOtpVerify = async (e) => {
    e?.preventDefault();
    if (!otpCode.trim()) { toast.error("Inserisci il codice"); return; }
    setBusy(true);
    try {
      await verifyPasswordless(email, otpCode.trim());
      toast.success("Accesso effettuato");
      nav("/");
    } catch (err) {
      toast.error(errMsg(err) || "Codice non valido");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-bg" data-testid="login-page">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 pv-grid-bg opacity-30" />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-brand-azure/20 blur-3xl" />
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <PrimeVisionLogo size={56} theme="light" hd />
          <div>
            <h2 className="text-4xl font-extrabold tracking-tight leading-tight" data-testid="login-headline">
              PrimeVision<br />
              <span className="text-brand-orange">Software.</span>
            </h2>
            <p className="mt-4 text-white/70 max-w-md" data-testid="login-tagline">
              La piattaforma clinica del medico oculista. Agenda, pazienti, visite, sedi e referti — tutti gestiti dall&apos;account del medico, in totale separazione tenant.
            </p>
            <div className="mt-8 space-y-4 max-w-md" data-testid="login-roles-explainer">
              <div className="flex gap-3 items-start">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <div className="font-bold text-sm">Admin Primevision</div>
                  <div className="text-xs text-white/60 mt-0.5">Crea e gestisce account medici, statistiche di utilizzo e audit amministrativo, senza accesso ai dati clinici.</div>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                  <Stethoscope size={18} />
                </div>
                <div>
                  <div className="font-bold text-sm">Medico oculista</div>
                  <div className="text-xs text-white/60 mt-0.5">Gestisce agenda, pazienti, visite, sedi e referti del proprio account.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-xs text-white/50">PrimeVision Software · accesso riservato ai professionisti registrati.</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 pb-28 sm:pb-32" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 7rem)" }}>
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center mb-8">
            <PrimeVisionLogo size={52} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy text-center">Accedi</h1>
          <p className="text-slate-500 mt-2 text-center">
            {step === "mfa" ? "Inserisci il codice OTP ricevuto per completare l'accesso." :
             step === "otpsent" ? "Ti abbiamo inviato un codice via SMS ed email." :
             mode === "otp" ? "Ricevi un codice OTP via SMS ed email — nessuna password." :
             "Inserisci le credenziali del tuo account."}
          </p>

          {step === "form" && (
            <div className="mt-6 grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-1" data-testid="login-tabs">
              <button
                data-testid="login-tab-password"
                onClick={() => { setMode("password"); setOtpCode(""); }}
                className={`py-2 rounded-md text-sm font-bold transition ${mode === "password" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500 hover:text-brand-navy"}`}
              >Password</button>
              <button
                data-testid="login-tab-otp"
                onClick={() => { setMode("otp"); setOtpCode(""); }}
                className={`py-2 rounded-md text-sm font-bold transition ${mode === "otp" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500 hover:text-brand-navy"}`}
              >Codice OTP</button>
            </div>
          )}

          {/* --- Step MFA (post password) --- */}
          {step === "mfa" && (
            <form onSubmit={submitMfa} className="mt-6 space-y-4" data-testid="login-mfa-form">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                Autenticazione a due fattori attiva. Il codice arriva via SMS al numero verificato e via email.
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-slate-500">Codice OTP</label>
                <input
                  data-testid="login-mfa-code"
                  autoFocus
                  inputMode="numeric" maxLength={8}
                  value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                  className="mt-1.5 w-full px-4 py-3 rounded-lg border border-[#E2E8F0] bg-white text-center font-mono text-2xl tracking-widest focus:outline-none focus:border-brand-azure focus:ring-2 focus:ring-brand-azure/30"
                  placeholder="123456"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} data-testid="login-trust-device" />
                Autentica questo dispositivo (non richiedere più il codice per 30 giorni)
              </label>
              <button
                data-testid="login-mfa-submit" disabled={busy}
                className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded-lg px-6 py-3 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={18} className="animate-spin" />} Conferma
              </button>
              <button type="button" onClick={() => { setStep("form"); setMfaChallenge(null); setOtpCode(""); localStorage.removeItem("pv_token"); }} className="w-full text-xs text-slate-500 hover:text-brand-navy py-2">
                Torna al login
              </button>
            </form>
          )}

          {/* --- OTP passwordless: request + verify --- */}
          {step === "otpsent" && (
            <form onSubmit={submitOtpVerify} className="mt-6 space-y-4" data-testid="login-otp-verify-form">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
                Se l&apos;account è valido riceverai un codice via SMS e email. Il codice è valido 10 minuti.
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-slate-500">Codice OTP</label>
                <input
                  data-testid="login-otp-code" autoFocus inputMode="numeric" maxLength={8}
                  value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                  className="mt-1.5 w-full px-4 py-3 rounded-lg border border-[#E2E8F0] bg-white text-center font-mono text-2xl tracking-widest"
                  placeholder="123456"
                />
              </div>
              <button data-testid="login-otp-verify-submit" disabled={busy}
                      className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded-lg px-6 py-3 disabled:opacity-60 flex items-center justify-center gap-2">
                {busy && <Loader2 size={18} className="animate-spin" />} Accedi con codice
              </button>
              <button type="button" onClick={() => { setStep("form"); setOtpCode(""); }} className="w-full text-xs text-slate-500 hover:text-brand-navy py-2">
                Torna indietro
              </button>
            </form>
          )}

          {step === "form" && (
          <form onSubmit={mode === "otp" ? submitOtpRequest : submit} className="mt-6 space-y-4" autoComplete="on">
            {/* Honeypot anti-bot (campo nascosto: i bot lo compileranno, gli umani no) */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
              aria-hidden="true"
              data-testid="login-honeypot"
            />
            <div>
              <label className="text-xs font-bold tracking-wider uppercase text-slate-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:border-brand-azure focus:ring-2 focus:ring-brand-azure/30 transition"
                placeholder="nome@studio.it"
              />
            </div>
            {mode === "password" && (
            <div>
              <label className="text-xs font-bold tracking-wider uppercase text-slate-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:border-brand-azure focus:ring-2 focus:ring-brand-azure/30 transition"
                placeholder="••••••••"
              />
            </div>
            )}
            {failCount >= 2 && (
              <div data-testid="login-challenge">
                <label className="text-xs font-bold tracking-wider uppercase text-slate-500">Verifica anti-bot</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="px-3 py-3 rounded-lg bg-brand-navy text-white font-mono font-bold select-none">{challenge.a} + {challenge.b} =</div>
                  <input
                    data-testid="login-challenge-input"
                    type="number"
                    required
                    value={challengeInput}
                    onChange={(e) => setChallengeInput(e.target.value)}
                    className="w-24 px-3 py-3 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:border-brand-azure text-center font-mono"
                    placeholder="?"
                  />
                </div>
              </div>
            )}
            {tsConfig.enabled && (
              <div data-testid="login-turnstile">
                <label className="text-xs font-bold tracking-wider uppercase text-slate-500">Verifica Cloudflare</label>
                <div ref={tsWidgetRef} className="mt-1.5" data-testid="login-turnstile-widget" />
                {!tsToken && (
                  <div className="text-[11px] text-slate-500 mt-1.5">Completa la verifica anti-bot per accedere.</div>
                )}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              disabled={busy || cooldownUntil > Date.now()}
              className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded-lg px-6 py-3 transition-colors active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 size={18} className="animate-spin" />} {mode === "otp" ? "Invia codice" : "Accedi"}
            </button>
            {cooldownUntil > Date.now() && (
              <div className="text-xs text-red-600 text-center" data-testid="login-cooldown">Troppi tentativi. Attendi qualche istante.</div>
            )}
          </form>
          )}

          <div className="mt-10 text-[11px] text-slate-400 leading-relaxed">
            Tutti gli accessi sono protetti da audit log, rate-limit, cifratura TLS e backup cifrato giornaliero. Le credenziali non vengono mostrate o suggerite dall&apos;interfaccia.
          </div>
        </div>
      </div>
    </div>
  );
}
